import { useTranslation } from 'next-i18next';

import type { CategoryTreeSubjectNameData } from '@/api/gen/_model/categoryTreeSubjectNameData';
import { useSchoolInfoShortTranslation } from '@/shared/hooks/useSchoolInfoShortTranslation';

/**
 * useCategoryTitle - 카테고리 정보를 기반으로 섹션 제목을 생성하는 hook
 *
 * @description
 * Hero Card에서 사용하는 것과 동일한 형식으로 카테고리 정보를 포맷합니다.
 * 예: "{초등 6-1} {수학} {비상} 퀴즈"
 *
 * @param categoryTreeData - 카테고리 트리 데이터
 * @returns 포맷된 카테고리 제목 문자열
 *
 * @example
 * ```tsx
 * const title = useCategoryTitle(categoryTreeData);
 * // "초등 6-1 수학 비상 퀴즈"
 * ```
 */
export const useCategoryTitle = (
  categoryTreeData?: CategoryTreeSubjectNameData,
): string => {
  const { t } = useTranslation(['common']);

  // 1. 학교 정보 (초등 6-1, 중1, 고1 등)
  const { schoolTypeText, schoolSemesterText, schoolGradeText } =
    useSchoolInfoShortTranslation({
      schoolInfo: {
        schoolType: categoryTreeData?.schoolType || '',
        schoolGrade: categoryTreeData?.schoolGrade || '',
        schoolSemester: categoryTreeData?.schoolSemester,
      },
    });

  // 카테고리 데이터가 없는 경우 기본 제목 반환
  if (!categoryTreeData) {
    return t('detail.categoryQuizzes.title');
  }

  const parts: string[] = [];

  const isShowSchoolGrade = Boolean(Number(categoryTreeData.schoolGrade));

  const schoolInfoText = `${schoolTypeText}${
    isShowSchoolGrade ? ` ${schoolGradeText}` : ''
  }${categoryTreeData.schoolSemester ? `-${schoolSemesterText}` : ''}`;

  if (schoolInfoText) {
    parts.push(schoolInfoText);
  }

  // 2. 과목명 (subSubjectName이 있으면 우선, 없으면 subjectName)
  const subjectName =
    categoryTreeData.subSubjectName || categoryTreeData.subjectName;
  if (subjectName) {
    parts.push(subjectName);
  }

  // 부분들을 공백으로 연결하고 "퀴즈" 추가

  return parts.length > 0
    ? t('detail.recommend.category.title', { category: parts.join(' ') })
    : t('detail.categoryQuizzes.title');
};
